/**
 * Admin audit-history page (Slice 2 CP16 Phase 1).
 *
 * READ-ONLY surface. Exposes the existing `mutation_events` table
 * (CP8 shape, migration 0007) so operators can browse who-did-what
 * history across the editorial-lifecycle mutation family (CP10–CP15).
 *
 * Narrow first-slice scope (see FISHSTANK_V2_CHECKPOINT_16_FIRST_SLICE.md §4.6):
 *   • four filter inputs — `target_table`, `target_id`, `action`, `page`;
 *   • one results table with seven columns (id, created_at,
 *     target_table, target_id, action, actor_cookie_hash_prefix,
 *     audit_mirror_deferred);
 *   • ZERO write affordances. No inline action buttons, no "undo",
 *     no "view full row JSON". Just list + filter + pagination.
 *   • `actor_cookie_hash_prefix` is rendered verbatim — 8 hex chars
 *     from the loader's `LEFT(actor_cookie_hash, 8)` projection. The
 *     full 64-char hash NEVER reaches the SPA.
 *
 * Deferred to later CP16 phases (intentionally absent):
 *   - row detail / JSON snapshot view;
 *   - `?since=` / `?actor_prefix=` filter axes;
 *   - export / download tooling;
 *   - cross-surface moderation dashboard.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type {
  MutationEventsAction,
  MutationEventsAdminQueueItem,
  MutationEventsTargetTable,
} from '@fishstank/contracts'
import {
  MUTATION_EVENTS_ACTIONS,
  MUTATION_EVENTS_TARGET_TABLES,
} from '@fishstank/contracts'
import { fetchMutationEventsAdminQueue } from '../lib/worker'
import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export function AuditHistoryPage() {
  const [page, setPage] = useState(1)
  const [targetTable, setTargetTable] = useState<
    MutationEventsTargetTable | ''
  >('')
  const [targetId, setTargetId] = useState<string>('')
  const [action, setAction] = useState<MutationEventsAction | ''>('')

  // The query-key mirrors CP15's `[surface, admin, queue, { ...filters }]`
  // convention so a future invalidation path (none in CP16) could
  // target the same key.
  const query = useQuery({
    queryKey: [
      'mutation-events',
      'admin',
      'queue',
      { page, targetTable, targetId, action },
    ],
    queryFn: () =>
      fetchMutationEventsAdminQueue({
        page,
        target_table: targetTable === '' ? null : targetTable,
        target_id: targetId.trim() === '' ? null : targetId.trim(),
        action: action === '' ? null : action,
      }),
  })

  const result = query.data

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">Audit history</h1>
          <p className="mt-1 text-sm text-slate-400">
            Read-only view of `mutation_events` (CP8 schema). Each row
            is an append-only audit entry emitted by a CP10–CP15
            editorial-lifecycle mutation. Actor is shown as the first 8
            hex chars of the cookie-hash only.
          </p>
        </div>
      </header>

      <FilterRow
        targetTable={targetTable}
        onTargetTableChange={(v) => {
          setTargetTable(v)
          setPage(1)
        }}
        targetId={targetId}
        onTargetIdChange={(v) => {
          setTargetId(v)
          setPage(1)
        }}
        action={action}
        onActionChange={(v) => {
          setAction(v)
          setPage(1)
        }}
      />

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="audit-history"
        labels={{
          loading: 'Loading audit history…',
          errorTitle: "Couldn't load audit history.",
          empty: 'No mutation-events match this filter.',
        }}
      >
        {(artifact) => (
          <>
            <AuditHistoryTable items={artifact.body.items} />
            <Pagination
              page={artifact.body.pagination.page}
              hasNext={artifact.body.pagination.has_next}
              totalReturned={artifact.body.pagination.total_returned}
              onChange={setPage}
            />
          </>
        )}
      </FetchedArtifact>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Filter row — target_table + target_id + action + (implicit) page.
// ────────────────────────────────────────────────────────────────────────────

function FilterRow({
  targetTable,
  onTargetTableChange,
  targetId,
  onTargetIdChange,
  action,
  onActionChange,
}: {
  targetTable: MutationEventsTargetTable | ''
  onTargetTableChange: (v: MutationEventsTargetTable | '') => void
  targetId: string
  onTargetIdChange: (v: string) => void
  action: MutationEventsAction | ''
  onActionChange: (v: MutationEventsAction | '') => void
}) {
  return (
    <div
      data-testid="audit-history-filter-row"
      className="mb-4 flex flex-wrap items-end gap-2 rounded-md border border-slate-800 bg-fishstank-surface/40 p-3 text-xs"
    >
      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          Target table
        </span>
        <select
          data-testid="audit-history-filter-target-table"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100"
          value={targetTable}
          onChange={(e) =>
            onTargetTableChange(
              e.target.value as MutationEventsTargetTable | '',
            )
          }
        >
          <option value="">Any</option>
          {MUTATION_EVENTS_TARGET_TABLES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          Target id
        </span>
        <input
          data-testid="audit-history-filter-target-id"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100 font-mono w-56"
          type="text"
          inputMode="text"
          placeholder="e.g. 42"
          value={targetId}
          onChange={(e) => onTargetIdChange(e.target.value)}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          Action
        </span>
        <select
          data-testid="audit-history-filter-action"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100"
          value={action}
          onChange={(e) =>
            onActionChange(e.target.value as MutationEventsAction | '')
          }
        >
          <option value="">Any</option>
          {MUTATION_EVENTS_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────────────────────────

function AuditHistoryTable({
  items,
}: {
  items: readonly MutationEventsAdminQueueItem[]
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="audit-history-table"
        data-audit-history-count={items.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-20">ID</th>
            <th className="px-3 py-2 text-left font-medium w-44">Created at</th>
            <th className="px-3 py-2 text-left font-medium w-40">Target table</th>
            <th className="px-3 py-2 text-left font-medium w-28">Target id</th>
            <th className="px-3 py-2 text-left font-medium w-20">Action</th>
            <th className="px-3 py-2 text-left font-medium w-28">Actor (8-hex)</th>
            <th className="px-3 py-2 text-left font-medium w-20">Mirror</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {items.map((row) => (
            <AuditHistoryRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AuditHistoryRow({ row }: { row: MutationEventsAdminQueueItem }) {
  return (
    <tr
      data-testid="audit-history-row"
      data-mutation-event-id={row.id}
      data-target-table={row.target_table}
      data-action={row.action}
      data-audit-mirror-deferred={String(row.audit_mirror_deferred)}
      className="hover:bg-slate-900/40"
    >
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.id}
      </td>
      <td
        data-testid="audit-history-row-created-at"
        className="px-3 py-1.5 font-mono text-xs text-slate-400"
      >
        {row.created_at}
      </td>
      <td
        data-testid="audit-history-row-target-table"
        className="px-3 py-1.5 font-mono text-xs text-slate-200"
      >
        {row.target_table}
      </td>
      <td
        data-testid="audit-history-row-target-id"
        className="px-3 py-1.5 font-mono text-xs text-slate-300"
      >
        {row.target_id}
      </td>
      <td
        data-testid="audit-history-row-action"
        className="px-3 py-1.5 text-xs text-slate-200"
      >
        {row.action}
      </td>
      <td
        data-testid="audit-history-row-actor-cookie-hash-prefix"
        className="px-3 py-1.5 font-mono text-[10px] text-slate-400"
      >
        {row.actor_cookie_hash_prefix}
      </td>
      <td
        data-testid="audit-history-row-mirror-status"
        className="px-3 py-1.5 text-xs"
      >
        {row.audit_mirror_deferred ? (
          <span className="text-amber-400">deferred</span>
        ) : (
          <span className="text-emerald-400">mirrored</span>
        )}
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Pagination
//
// Structural clone of XSourceEntries / Messages / Tts pager. Prev disabled
// when page === 1; Next disabled when !has_next. No page-count label — the
// bounded total is a truncation signal, not a real count (see
// `MUTATION_EVENTS_ADMIN_QUEUE_MAX_PAGES` in the loader).
// ────────────────────────────────────────────────────────────────────────────

function Pagination({
  page,
  hasNext,
  totalReturned,
  onChange,
}: {
  page: number
  hasNext: boolean
  totalReturned: number
  onChange: (p: number) => void
}) {
  const prevDisabled = page <= 1
  const nextDisabled = !hasNext
  return (
    <div
      data-testid="audit-history-pagination"
      className="mt-4 flex items-center justify-between text-xs text-slate-400"
    >
      <span>
        Page {page} · {totalReturned} row{totalReturned === 1 ? '' : 's'}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="audit-history-pagination-prev"
          disabled={prevDisabled}
          onClick={() => onChange(Math.max(1, page - 1))}
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          Prev
        </button>
        <button
          type="button"
          data-testid="audit-history-pagination-next"
          disabled={nextDisabled}
          onClick={() => onChange(page + 1)}
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          Next
        </button>
      </div>
    </div>
  )
}
